import { useState, useMemo, useEffect, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  ArrowLeft, Coins, TrendingDown, Sparkles, Loader2, MessageCircle,
  Lock, Gem, BarChart3, Compass,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WHATSAPP = "393497173896";
const WA_MSG = "Ciao Alex, ho fatto il calcolatore e vorrei parlarne.";

const GOLD_PRICE_FALLBACK = 112;

const SCENARIOS = [
  { key: "prudente", label: "Prudente", rate: 1.5, note: "" },
  { key: "media", label: "Media storica (10 anni)", rate: 2.0, note: "ISTAT" },
  { key: "elevato", label: "Scenario elevato (tipo 2022-23)", rate: 5.7, note: "" },
];

const euro = (n) => Math.round(n).toLocaleString("it-IT") + " €";
const euroK = (n) => Math.round(n / 1000) + "k";
const grammiFmt = (n) => Number(n).toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const inflFmt = (n) => Number(n).toFixed(1).replace(".", ",");

// Attribuzione funnel: legge gli UTM e li conserva per la sessione
function getProvenienza() {
  try {
    const p = new URLSearchParams(window.location.search);
    const src = (p.get("utm_source") || "").trim();
    const med = (p.get("utm_medium") || "").trim();
    const camp = (p.get("utm_campaign") || "").trim();
    const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
    if (src) {
      const prov = [cap(src), med, camp].filter(Boolean).join(" · ");
      sessionStorage.setItem("ag_prov", prov);
      return prov;
    }
    return sessionStorage.getItem("ag_prov") || "Diretto";
  } catch {
    return "Diretto";
  }
}

// A/B test telefono (Fase D): variante stabile per sessione
function getVariant() {
  try {
    let v = sessionStorage.getItem("ag_var");
    if (v !== "A" && v !== "B") {
      v = Math.random() < 0.5 ? "A" : "B";
      sessionStorage.setItem("ag_var", v);
    }
    return v;
  } catch {
    return "A";
  }
}

function Section({ icon: Icon, title, children, testid }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 p-6 md:p-7" data-testid={testid}>
      <h3 className="flex items-center gap-2.5 font-head font-bold text-xl md:text-2xl text-zinc-50 mb-4">
        <Icon size={22} className="text-yellow-500 shrink-0" /> {title}
      </h3>
      {children}
    </div>
  );
}

export default function PianoAzione() {
  const [capitale, setCapitale] = useState(15000);
  const [anni, setAnni] = useState(5);
  const [inflazione, setInflazione] = useState(2.0);
  const [activeScenario, setActiveScenario] = useState("media");
  const [report, setReport] = useState(null);
  const [form, setForm] = useState({ nome: "", email: "", telefono: "", privacy: false });
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [gold, setGold] = useState({ eur_per_gram: GOLD_PRICE_FALLBACK, updated_at: null, stale: true, loading: true });
  const [history, setHistory] = useState(null);
  const [provenienza] = useState(getProvenienza);
  const [variante] = useState(getVariant);
  const [consenso, setConsenso] = useState(false);
  const avvioSent = useRef(false);

  const scenarioLabel = () =>
    SCENARIOS.find((s) => s.key === activeScenario)?.label ||
    (activeScenario === "custom" ? "Personalizzato" : "");

  // SEO / social meta (titolo, description, og)
  useEffect(() => {
    const prevTitle = document.title;
    const title = "Calcolatore gratuito | Alex Gold";
    const desc = "Scopri in 2 minuti quanto valgono davvero i tuoi risparmi. Per Partite IVA senza TFR.";
    document.title = title;
    const setMeta = (selector, attr, key, content) => {
      let el = document.head.querySelector(selector);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta('meta[name="description"]', "name", "description", desc);
    setMeta('meta[property="og:title"]', "property", "og:title", title);
    setMeta('meta[property="og:description"]', "property", "og:description", desc);
    setMeta('meta[property="og:type"]', "property", "og:type", "website");
    return () => { document.title = prevTitle; };
  }, []);

  useEffect(() => {
    axios.get(`${API}/gold-price`)
      .then(({ data }) => setGold({ ...data, loading: false }))
      .catch(() => setGold((g) => ({ ...g, loading: false })));
    axios.get(`${API}/gold-history`)
      .then(({ data }) => setHistory(data))
      .catch(() => {});
  }, []);

  const price = Number(gold.eur_per_gram) || GOLD_PRICE_FALLBACK;

  const results = useMemo(() => {
    const C = Number(capitale) || 0;
    const r = Number(inflazione) / 100;
    const n = Number(anni);
    const realValue = C / Math.pow(1 + r, n);
    const perdita = realValue - C;
    const perditaPct = C > 0 ? (1 - realValue / C) * 100 : 0;
    const grammi = price > 0 ? C / price : 0;
    const chart = Array.from({ length: n + 1 }, (_, t) => ({
      anno: `${t}a`,
      reale: Math.round(C / Math.pow(1 + r, t)),
    }));
    return { C, r, n, realValue, perdita, perditaPct, grammi, chart };
  }, [capitale, anni, inflazione, price]);

  // Grafico oro vs inflazione ultimi 10 anni (dati reali editabili), indice base 100
  const histChart = useMemo(() => {
    const pts = history?.points ? [...history.points].sort((a, b) => a.anno - b.anno) : [];
    if (pts.length < 2) return [];
    const base = pts[0].oro_eur_oz;
    let infl = 100;
    return pts.map((p, i) => {
      if (i > 0) infl = infl * (1 + (Number(p.inflazione) || 0) / 100);
      return {
        anno: String(p.anno),
        oro: base ? Math.round((p.oro_eur_oz / base) * 100) : 100,
        inflazione: Math.round(infl),
      };
    });
  }, [history]);

  const pickScenario = (s) => { setActiveScenario(s.key); setInflazione(s.rate); };

  const generate = () => {
    if (!capitale || Number(capitale) <= 0) {
      toast.error("Inserisci il capitale fermo sul conto");
      return;
    }
    setReport(null);
    setPreviewOpen(true);
    if (!avvioSent.current) {
      avvioSent.current = true;
      axios.post(`${API}/piano-azione/track`, {
        tipo: "avvio",
        capitale: Number(capitale) || 0,
        anni: Number(anni),
        inflazione: Number(inflazione),
        scenario: scenarioLabel(),
        provenienza,
        variante,
      }).catch(() => {});
    }
    setTimeout(() => { document.getElementById("pa-preview")?.scrollIntoView({ behavior: "smooth" }); }, 120);
  };

  const submitReport = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.email) { toast.error("Nome ed email sono obbligatori"); return; }
    if (!form.privacy) { toast.error("Accetta l'informativa privacy per continuare"); return; }
    if (variante === "B" && !form.telefono) { toast.error("Inserisci un recapito telefonico per ricevere il report"); return; }
    setSending(true);
    const r = results;
    const inflStr = `${inflFmt(inflazione)}%`;
    const valReale = euro(r.realValue);
    const grammiStr = grammiFmt(r.grammi);
    const scenario = SCENARIOS.find((s) => s.key === activeScenario)?.label || (activeScenario === "custom" ? "Personalizzato" : "");
    const messaggio =
      `[Piano Azione] Capitale ${euro(r.C)} · orizzonte ${r.n} anni · inflazione ${inflStr} · ` +
      `valore reale ${valReale} · ${grammiStr} g oro. Report dal calcolatore.`;
    try {
      await axios.post(`${API}/leads`, {
        tipo: "cliente",
        nome: form.nome,
        email: form.email,
        telefono: form.telefono,
        provenienza,
        consenso_follow_up: consenso,
        variante_telefono: variante,
        patrimonio: euro(r.C),
        messaggio,
        report_capitale: euro(r.C),
        report_anni: r.n,
        report_perdita: euro(Math.abs(r.perdita)),
        report_perdita_pct: `${inflFmt(r.perditaPct)}% in ${r.n} anni`,
        report_valore_reale: valReale,
        report_grammi: grammiStr,
        report_inflazione: inflStr,
      });
      axios.post(`${API}/piano-azione/track`, {
        tipo: "completato",
        capitale: r.C, anni: r.n, inflazione: Number(inflazione), scenario, provenienza, variante,
      }).catch(() => {});
      setReport({ ...r, nome: form.nome, inflStr, prezzo: price });
      toast.success("Report pronto! Te l'ho inviato anche via email.");
      setTimeout(() => { document.getElementById("pa-report")?.scrollIntoView({ behavior: "smooth" }); }, 150);
    } catch {
      toast.error("Errore nell'invio. Riprova.");
    } finally {
      setSending(false);
    }
  };

  const firstName = (report?.nome || "").split(" ")[0];
  const openWhatsApp = () => window.open(`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(WA_MSG)}`, "_blank");

  return (
    <div className="bg-zinc-950 text-zinc-50 min-h-screen overflow-x-hidden" data-testid="piano-azione-page">
      <Toaster theme="dark" position="top-right" />

      <header className="border-b border-zinc-800 px-4 md:px-8 py-4 flex items-center justify-between sticky top-0 bg-zinc-950/95 backdrop-blur z-20">
        <div className="flex items-center gap-3">
          <div className="bg-yellow-500 p-2"><Coins size={20} className="text-black" /></div>
          <div>
            <h1 className="font-head font-extrabold text-xl tracking-tight leading-none">ALEX GOLD</h1>
            <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500">Calcolatore</p>
          </div>
        </div>
        <Link to="/" data-testid="link-home"
          className="flex items-center gap-1.5 text-zinc-400 hover:text-yellow-500 text-sm transition-colors">
          <ArrowLeft size={15} /> Torna al sito
        </Link>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-8 py-10 md:py-14">
        {/* Hero */}
        <div className="text-center mb-10">
          <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-yellow-600 mb-4">
            Report gratuito · Calcolatore · dati reali ISTAT
          </p>
          <h2 className="font-head font-extrabold text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight">
            Prima di costruire la tua liquidazione, scopri{" "}
            <span className="text-yellow-500">quanto valgono davvero</span> i soldi che hai già.
          </h2>
          <p className="text-base text-zinc-400 mt-5 max-w-2xl mx-auto">
            Per Partite IVA senza TFR. Inserisci i tuoi risparmi: in 2 minuti vedi il loro valore reale nel tempo.
          </p>
          <div className="inline-flex items-center gap-2 mt-6 border border-zinc-800 bg-zinc-900 px-4 py-2 rounded-full" data-testid="gold-price">
            <Coins size={15} className="text-yellow-500" />
            <span className="text-sm text-zinc-400">Oro oggi:</span>
            <span className="text-sm font-semibold text-yellow-500" data-testid="gold-price-value">
              {gold.loading ? "…" : `${price.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/g`}
            </span>
            {!gold.loading && (
              gold.stale ? (
                <span className="flex items-center gap-1 text-[10px] font-mono uppercase text-zinc-500">
                  <span className="w-1.5 h-1.5 rounded-full bg-zinc-500" /> rif.
                </span>
              ) : (
                <span className="flex items-center gap-1 text-[10px] font-mono uppercase text-emerald-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> live
                </span>
              )
            )}
          </div>
        </div>

        {/* Calculator */}
        <div className="bg-zinc-900 border border-zinc-800 p-6 md:p-8">
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Capitale fermo sul conto</label>
              <div className="flex items-center border border-zinc-700 bg-zinc-950 focus-within:border-yellow-500 transition-colors">
                <span className="px-3 text-zinc-500 text-lg">€</span>
                <input
                  type="number" value={capitale} placeholder="15000"
                  onChange={(e) => setCapitale(e.target.value)}
                  data-testid="capitale-input"
                  className="w-full bg-transparent py-3 pr-3 text-lg text-zinc-100 focus:outline-none"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-2">
                Per quanti anni? <span className="text-yellow-500 font-semibold">{anni}</span>
              </label>
              <input
                type="range" min="1" max="30" value={anni}
                onChange={(e) => setAnni(Number(e.target.value))}
                data-testid="anni-slider"
                className="w-full accent-yellow-500 mt-4"
              />
              <div className="flex justify-between text-[11px] font-mono text-zinc-600 mt-1">
                <span>1 anno</span><span>30 anni</span>
              </div>
            </div>
          </div>

          <label className="block text-sm text-zinc-400 mb-3">Scenario di inflazione</label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            {SCENARIOS.map((s) => (
              <button
                key={s.key} onClick={() => pickScenario(s)}
                data-testid={`scenario-${s.key}`}
                className={`text-left p-4 border transition-colors ${
                  activeScenario === s.key
                    ? "border-yellow-500 bg-yellow-500/10"
                    : "border-zinc-700 bg-zinc-950 hover:border-zinc-500"}`}
              >
                <p className={`text-sm font-medium ${activeScenario === s.key ? "text-yellow-500" : "text-zinc-200"}`}>{s.label}</p>
                <p className="text-[11px] font-mono text-zinc-500 mt-1">
                  {inflFmt(s.rate)}%{s.note ? ` · ${s.note}` : ""}
                </p>
              </button>
            ))}
          </div>

          <div className="flex items-center gap-4 mb-6">
            <span className="text-sm text-zinc-400 shrink-0">Personalizza:</span>
            <input
              type="range" min="0.5" max="8" step="0.1" value={inflazione}
              onChange={(e) => { setInflazione(Number(e.target.value)); setActiveScenario("custom"); }}
              data-testid="custom-inflation-slider"
              className="w-full accent-yellow-500"
            />
            <span className="text-sm font-semibold text-yellow-500 shrink-0 w-14 text-right">
              {Number(inflazione).toFixed(1)}%
            </span>
          </div>

          <button
            onClick={generate}
            data-testid="genera-report-button"
            className="w-full flex items-center justify-center gap-2 bg-yellow-500 text-black font-semibold hover:bg-yellow-400 py-4 text-base transition-colors rounded-full"
          >
            <Sparkles size={18} /> Scopri quanto valgono davvero
          </button>
        </div>

        {/* Anteprima + Form contatti */}
        <AnimatePresence>
          {previewOpen && !report && (
            <motion.div
              id="pa-preview"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              className="mt-8 space-y-6"
            >
              {/* Card anteprima */}
              <div className="bg-zinc-900 border border-yellow-700/40 p-6 md:p-8 text-center" data-testid="preview-card">
                <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-yellow-600 mb-4">La tua anteprima</p>
                <p className="text-lg md:text-xl text-zinc-200 leading-relaxed">
                  I tuoi <strong className="text-zinc-50">{euro(results.C)}</strong> tra <strong className="text-zinc-50">{results.n} anni</strong> compreranno come
                </p>
                <p className="font-head font-extrabold text-4xl md:text-6xl text-yellow-500 my-3" data-testid="preview-valore-reale">
                  {euro(results.realValue)}
                </p>
                <p className="text-lg md:text-xl text-zinc-200">di oggi.</p>
                <p className="text-base text-zinc-400 leading-relaxed mt-6 max-w-xl mx-auto">
                  Oggi con <strong className="text-zinc-200">{euro(results.C)}</strong> puoi avere{" "}
                  <strong className="text-yellow-500" data-testid="preview-grammi">{grammiFmt(results.grammi)} g</strong> di oro fisico,
                  un bene che non dipende da nessuna banca.
                </p>
                <p className="text-sm text-zinc-500 mt-4">
                  Nel report completo vedi come proteggere il loro valore, anno per anno.
                </p>
              </div>

              {/* Form contatti */}
              <div className="bg-zinc-900 border border-zinc-800 p-6 md:p-8" data-testid="contact-form-block">
                <h3 className="font-head font-extrabold text-2xl md:text-3xl text-center mb-6">
                  Dove ti invio il report completo?
                </h3>
                <form onSubmit={submitReport} className="max-w-md mx-auto space-y-3">
                  <input type="text" placeholder="Nome e cognome" value={form.nome}
                    onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="form-nome"
                    className="w-full bg-zinc-950 border border-zinc-700 focus:border-yellow-500 py-3 px-3 text-zinc-100 focus:outline-none transition-colors" />
                  <input type="email" placeholder="La tua email" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="form-email"
                    className="w-full bg-zinc-950 border border-zinc-700 focus:border-yellow-500 py-3 px-3 text-zinc-100 focus:outline-none transition-colors" />
                  <p className="text-[11px] text-zinc-500 pt-1">Ti scrivo solo su WhatsApp, niente chiamate a sorpresa.</p>
                  <input type="text" placeholder={variante === "B" ? "Telefono / WhatsApp (per ricevere il report)" : "Telefono / WhatsApp (facoltativo)"} value={form.telefono}
                    onChange={(e) => setForm({ ...form, telefono: e.target.value })} data-testid="form-telefono"
                    className="w-full bg-zinc-950 border border-zinc-700 focus:border-yellow-500 py-3 px-3 text-zinc-100 focus:outline-none transition-colors" />
                  <label className="flex items-start gap-2 text-xs text-zinc-500 cursor-pointer">
                    <input type="checkbox" checked={form.privacy}
                      onChange={(e) => setForm({ ...form, privacy: e.target.checked })} data-testid="form-privacy"
                      className="mt-0.5 accent-yellow-500" />
                    <span>Ho letto e accetto l'informativa sulla privacy e acconsento al trattamento dei miei dati per ricevere il report ed essere ricontattato.</span>
                  </label>
                  <label className="flex items-start gap-2 text-xs text-zinc-500 cursor-pointer">
                    <input type="checkbox" checked={consenso}
                      onChange={(e) => setConsenso(e.target.checked)} data-testid="form-consenso"
                      className="mt-0.5 accent-yellow-500" />
                    <span>Sì, inviami anche qualche contenuto utile per proteggere il potere d'acquisto dei miei risparmi (facoltativo, puoi disiscriverti quando vuoi).</span>
                  </label>
                  <button type="submit" disabled={sending} data-testid="form-submit"
                    className="w-full flex items-center justify-center gap-2 bg-yellow-500 text-black font-semibold hover:bg-yellow-400 disabled:opacity-70 py-3.5 transition-colors rounded-full">
                    {sending ? <Loader2 size={17} className="animate-spin" /> : <Sparkles size={17} />}
                    Inviami il report completo
                  </button>
                  <p className="flex items-center justify-center gap-1.5 text-[11px] text-zinc-500 pt-1">
                    <Lock size={11} /> Dati riservati · niente spam · senza impegno
                  </p>
                </form>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Report completo */}
        <AnimatePresence>
          {report && (
            <motion.div
              id="pa-report"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }} className="mt-10 space-y-6" data-testid="report-section"
            >
              <div className="text-center">
                <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-yellow-600 mb-2">Report personale</p>
                <h2 className="font-head font-extrabold text-3xl sm:text-4xl" data-testid="report-title">
                  Il tuo report, {firstName}
                </h2>
              </div>

              {/* Sezione 1 */}
              <Section icon={TrendingDown} title="📉 Il valore reale dei tuoi risparmi" testid="report-sez1">
                <div className="h-64 md:h-72" data-testid="report-chart-reale">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={report.chart} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                      <XAxis dataKey="anno" stroke="#71717a" fontSize={12} />
                      <YAxis stroke="#71717a" fontSize={12} tickFormatter={euroK} />
                      <Tooltip
                        contentStyle={{ background: "#18181b", border: "1px solid #3f3f46", borderRadius: 0, color: "#fafafa" }}
                        formatter={(v) => euro(v)} labelStyle={{ color: "#a1a1aa" }} />
                      <Line type="monotone" dataKey="reale" name="Valore reale" stroke="#eab308" strokeWidth={2.5} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-zinc-300 leading-relaxed mt-4">
                  Con l'inflazione al <strong className="text-zinc-100">{report.inflStr}</strong>, in{" "}
                  <strong className="text-zinc-100">{report.n} anni</strong> i tuoi <strong className="text-zinc-100">{euro(report.C)}</strong> compreranno
                  come <strong className="text-yellow-500">{euro(report.realValue)}</strong> di oggi.
                </p>
              </Section>

              {/* Sezione 2 */}
              <Section icon={Gem} title="🪙 Il tuo capitale in oro fisico" testid="report-sez2">
                <p className="text-zinc-300 leading-relaxed">
                  Al prezzo di oggi (<strong className="text-yellow-500">{report.prezzo.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/g</strong>),{" "}
                  <strong className="text-zinc-100">{euro(report.C)}</strong> corrispondono a{" "}
                  <strong className="text-yellow-500">{grammiFmt(report.grammi)} g</strong> di oro fisico.
                  Un bene tangibile, a tuo nome, custodito fuori dal sistema bancario.
                </p>
              </Section>

              {/* Sezione 3 */}
              <Section icon={BarChart3} title="📊 Cosa ha fatto l'oro negli ultimi 10 anni" testid="report-sez3">
                {histChart.length > 0 ? (
                  <>
                    <div className="h-64 md:h-72" data-testid="report-chart-storico">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={histChart} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                          <XAxis dataKey="anno" stroke="#71717a" fontSize={12} />
                          <YAxis stroke="#71717a" fontSize={12} />
                          <Tooltip
                            contentStyle={{ background: "#18181b", border: "1px solid #3f3f46", borderRadius: 0, color: "#fafafa" }}
                            formatter={(v, name) => [`${v} (base 100)`, name]} labelStyle={{ color: "#a1a1aa" }} />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                          <Line type="monotone" dataKey="oro" name="Oro (EUR)" stroke="#eab308" strokeWidth={2.5} dot={false} />
                          <Line type="monotone" dataKey="inflazione" name="Inflazione ISTAT" stroke="#f87171" strokeWidth={2.5} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    <p className="text-[11px] text-zinc-500 mt-3">
                      Indice base 100. {history?.fonte || "Oro: World Gold Council. Inflazione: ISTAT (NIC)."}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-zinc-500">Dati storici non disponibili al momento.</p>
                )}
                <p className="text-[11px] text-zinc-600 mt-2">I dati passati non garantiscono risultati futuri.</p>
              </Section>

              {/* Sezione 4 */}
              <Section icon={Compass} title="🧭 Il Metodo Alex Gold, in 3 fasi" testid="report-sez4">
                <ol className="space-y-4">
                  {[
                    ["Mappa", "guardiamo insieme la tua situazione reale."],
                    ["Piano", "costruiamo un accumulo su misura, mensile o con un capitale unico."],
                    ["Riserva", "la tua liquidazione in oro fisico, che cresce con te."],
                  ].map(([t, d], i) => (
                    <li key={t} className="flex gap-3">
                      <span className="shrink-0 w-7 h-7 rounded-full bg-yellow-500 text-black font-bold flex items-center justify-center text-sm">{i + 1}</span>
                      <p className="text-zinc-300 leading-relaxed pt-0.5"><strong className="text-zinc-100">{t}:</strong> {d}</p>
                    </li>
                  ))}
                </ol>
                <a href="/metodo" data-testid="scopri-metodo-btn"
                  className="inline-flex items-center justify-center gap-2 mt-5 border border-zinc-700 text-zinc-200 hover:border-yellow-500 hover:text-yellow-500 py-2.5 px-5 rounded-full text-sm transition-colors">
                  Scopri il Metodo Alex Gold
                </a>
              </Section>

              {/* Sezione 5 */}
              <Section icon={MessageCircle} title="📲 Prossimo passo" testid="report-sez5">
                <p className="text-zinc-300 leading-relaxed mb-5">
                  Entro 24 ore ti scrivo su WhatsApp per una chiacchierata di 15 minuti, senza impegno.
                </p>
                <button onClick={openWhatsApp} data-testid="whatsapp-cta"
                  className="w-full sm:w-auto flex items-center justify-center gap-2 bg-emerald-600 text-white font-semibold hover:bg-emerald-500 py-3.5 px-7 rounded-full transition-colors">
                  <MessageCircle size={18} /> Scrivimi subito su WhatsApp
                </button>
                <p className="text-[11px] text-zinc-600 mt-5">Alex Gold collabora con operatori OAM autorizzati.</p>
              </Section>
            </motion.div>
          )}
        </AnimatePresence>

        <p className="text-center text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-600 mt-12">
          Alex Gold · Protezione Patrimoniale · Dati: ISTAT, World Gold Council
        </p>
      </main>
    </div>
  );
}
